// Story 4.5 — TanStack Query mutation hook for POST /api/v1/account/handle.
//
// Mirrors Story 3.7 useUpdatePreferences.ts conventions:
//   * Generates the fetch payload from the variables object.
//   * Calls attachRequestIdToScope() for Sentry correlation per Story 1.8.
//   * On non-OK: parses { error: { code, message, field? } } shape +
//     throws so consumers can `.catch` or read useMutation `.error`.
//   * On success: invalidates ["account-me"] so useAccountMe() re-fetches
//     the new handle value.
//
// NO optimistic-update pattern (deliberate deviation from Story 4.4
// useShareToggle): handle changes are destructive enough to warrant
// server-confirmation-first. The Spa-side handle-change confirmation
// modal already warns the user about the 90-day redirect; the
// mutation either succeeds (200 with the new handle) or fails (409
// HANDLE_TAKEN / 400 VALIDATION_FAILED) — we want the UI to reflect
// only the confirmed-by-server state.
//
// Typed errors:
//   * 409 HANDLE_TAKEN — the requested handle is currently held or
//     sits in another user's active 90-day redirect window.
//   * 400 VALIDATION_FAILED on field `handle` — invalid kebab-case
//     format / length / leading-trailing dash / uppercase.
//   * 401 — should be caught by session middleware before reaching
//     here, but the hook still propagates it as a typed error so the
//     UI can surface "Sign in to change your handle".

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface ChangeHandleVariables {
  handle: string;
}

export interface ChangeHandleResponseData {
  handle: string;
}

export interface ChangeHandleError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

async function postChangeHandle(
  variables: ChangeHandleVariables,
): Promise<ChangeHandleResponseData> {
  const response = await fetch("/api/v1/account/handle", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ChangeHandleError = {
      code: "HANDLE_CHANGE_FAILED",
      message: `Handle change failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: ChangeHandleResponseData };
  return body.data;
}

export function useChangeHandle() {
  const queryClient = useQueryClient();
  return useMutation<
    ChangeHandleResponseData,
    ChangeHandleError,
    ChangeHandleVariables
  >({
    mutationFn: postChangeHandle,
    onSuccess: () => {
      // The handle change flips users.handle + (on subsequent
      // changes) inserts a user_handle_history row. The SPA's
      // AccountMe envelope carries the handle field, so an
      // invalidation here drives the AccountProfilePage view-link
      // anchor to refresh to the new /u/{handle} URL.
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
    },
  });
}
